import { apiFetch } from './api';
import type { StatusDocumento, TipoDocumento } from './ModeloDocumentoService';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface DocumentoEmitidoItem {
    codigo: string | null;
    descricao: string;
    quantidade: number;
    precoUnitario: number;
    total: number;
}

export interface DocumentoEmitido {
    documentoEmitidoId: number;
    conversaId: number;
    modeloDocumentoId: number;
    nomeModelo: string | null;
    numero: number;
    tipo: TipoDocumento;
    titulo: string;
    valorTotal: number;
    /** Só a data (yyyy-MM-dd) ou null. */
    validade: string | null;
    status: StatusDocumento;
    usuarioEmissorId: number;
    nomeUsuarioEmissor: string | null;
    dataEmissao: string;
    itens: DocumentoEmitidoItem[];
}

export interface EmitirDocumentoDTO {
    conversaId: number;
    modeloDocumentoId: number;
    /** Null usa a validade padrão do modelo; zero ou negativo emite sem validade. */
    validadeDias: number | null;
    /** Null gera "Proposta nº 0042 - <organização ou contato>". */
    titulo: string | null;
}

export interface ArquivoPdf {
    blob: Blob;
    nome: string;
}

interface ApiResponse<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

const json = { 'Content-Type': 'application/json' };

const lerOuFalhar = async <T,>(res: Response, padrao: string): Promise<ApiResponse<T>> => {
    const data = await res.json().catch(() => null);
    if (!res.ok || !data?.sucesso) throw new Error(data?.mensagem || padrao);
    return data;
};

/** `attachment; filename="x.pdf"; filename*=UTF-8''x.pdf` → `x.pdf`. */
const nomeDoCabecalho = (res: Response, padrao: string): string => {
    const cd = res.headers.get('Content-Disposition') || '';
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(cd);
    if (utf8) { try { return decodeURIComponent(utf8[1]); } catch { /* cai no simples */ } }
    const simples = /filename="?([^";]+)"?/i.exec(cd);
    return simples ? simples[1] : padrao;
};

/* O PDF vem de endpoint autenticado por cookie: não dá para pôr a URL num
   `<a href>` nem num `window.open` direto, porque a resposta de erro seria o
   JSON padrão. Baixa como blob e a tela decide se abre numa aba ou salva. */
const baixarPdf = async (url: string, nomePadrao: string): Promise<ArquivoPdf> => {
    const res = await apiFetch(url);
    if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.mensagem || 'Não foi possível obter o PDF.');
    }
    return { blob: await res.blob(), nome: nomeDoCabecalho(res, nomePadrao) };
};

export const DocumentoEmitidoService = {
    listarPorConversa: async (conversaId: number): Promise<ApiResponse<DocumentoEmitido[]>> => {
        const res = await apiFetch(`${API_URL}/api/DocumentoEmitido/Conversa/${conversaId}`);
        if (!res.ok) {
            if (res.status === 400 || res.status === 404) return { sucesso: true, mensagem: '', dados: [] };
            throw new Error('Erro ao buscar documentos da oportunidade');
        }
        return res.json();
    },

    emitir: async (dto: EmitirDocumentoDTO): Promise<ApiResponse<DocumentoEmitido>> => {
        const res = await apiFetch(`${API_URL}/api/DocumentoEmitido/Emitir`, {
            method: 'POST', headers: json, body: JSON.stringify(dto),
        });
        return lerOuFalhar(res, 'Não foi possível emitir o documento.');
    },

    /** PDF da prévia, sem gravar nada. */
    previa: (modeloDocumentoId: number, conversaId: number): Promise<ArquivoPdf> =>
        baixarPdf(`${API_URL}/api/DocumentoEmitido/Previa/${modeloDocumentoId}/${conversaId}`, 'previa.pdf'),

    pdf: (documentoEmitidoId: number): Promise<ArquivoPdf> =>
        baixarPdf(`${API_URL}/api/DocumentoEmitido/${documentoEmitidoId}/Pdf`, 'documento.pdf'),

    atualizarStatus: async (documentoEmitidoId: number, status: StatusDocumento): Promise<ApiResponse<null>> => {
        const res = await apiFetch(`${API_URL}/api/DocumentoEmitido/${documentoEmitidoId}/Status`, {
            method: 'PATCH', headers: json, body: JSON.stringify({ status }),
        });
        return lerOuFalhar(res, 'Não foi possível atualizar o status.');
    },

    remover: async (documentoEmitidoId: number): Promise<ApiResponse<null>> => {
        const res = await apiFetch(`${API_URL}/api/DocumentoEmitido/Remover/${documentoEmitidoId}`, { method: 'DELETE' });
        return lerOuFalhar(res, 'Não foi possível remover o documento.');
    },
};

export default DocumentoEmitidoService;
